// StableSurfaces Dependent composite reference D13 (docs/stable-surfaces/model.md): target schemas and the one target compare.
// No `node:crypto` in its import closure (browser graph).
import { z } from 'zod'
import type { SightmapDoc } from '../polish/sightmap/schema.ts'
import { parseDefinitionId, parseSurfaceId, type SurfaceId } from './id.ts'
import { resolveDefinition, sourceDecidesKeys, type CanonicalTarget } from './resolve-definition.ts'

export const COMPONENT_PATH = z.array(z.object({
  componentId: z.string().min(1).transform(parseDefinitionId), params: z.record(z.string()).optional(),
}).strict()).min(1)
export const SIGHTMAP_TARGET = z.object({ surfaceId: z.string().min(1).transform(parseSurfaceId), path: COMPONENT_PATH, selector: z.string().trim().min(1).optional(), paramsSource: z.literal('observation').optional(), observationRef: z.string().min(1).optional() }).strict()
// controlId is read compatibility for existing catalogs. New bindings carry the complete path.
export const COMPONENT_TARGET = z.object({
  surfaceId: z.string().min(1).transform(parseSurfaceId), controlId: z.string().min(1).transform(parseDefinitionId).optional(), path: COMPONENT_PATH.optional(), selector: z.string().trim().min(1).optional(), paramsSource: z.literal('observation').optional(), observationRef: z.string().min(1).optional(),
}).refine(target => !!target.path?.length || !!target.controlId, 'A Sightmap target needs a component path')
export type ComponentTarget = z.infer<typeof COMPONENT_TARGET>
/** Observation handles are capabilities for one captured page, never shared tool identity. */
export function durableTarget(target: ComponentTarget): ComponentTarget {
  const { observationRef: _observationRef, ...durable } = target
  return durable
}
/** The only target equality key: the address without its observation handle. When the map resolves the
 * target, the address of its canonical form, so an alias and its current id name one referent.
 * Unresolved targets keep their raw address. */
export function canonicalKey(target: ComponentTarget, doc: SightmapDoc | null): string {
  const { observationRef: _observationRef, ...address } = target
  const resolution = doc ? resolveDefinition(address, doc) : null
  return resolution?.kind === 'unique' ? addressKey(canonicalForm(address, resolution.hit), sourceDecidesKeys(resolution.hit)) : addressKey(address, false)
}
export function sameTarget(a: ComponentTarget | undefined, b: ComponentTarget | undefined, doc: SightmapDoc | null): boolean {
  if (!a || !b) return a === b
  return canonicalKey(a, doc) === canonicalKey(b, doc)
}
/** The element a target names, regardless of which Surface's scoping addresses it: the canonical address
 * without its Surface. One control rendered on several Surfaces acquires a Surface-scoped address per
 * Surface; these compare equal, so a lookup can accept any of them. */
export function elementKey(target: ComponentTarget, doc: SightmapDoc | null): string {
  const { observationRef: _observationRef, ...address } = target
  const resolution = doc ? resolveDefinition(address, doc) : null
  const canonical = resolution?.kind === 'unique' ? canonicalForm(address, resolution.hit) : address
  const { surfaceId: _surfaceId, ...element } = canonical
  return addressKey(element, resolution?.kind === 'unique' ? sourceDecidesKeys(resolution.hit) : false)
}
/** Current surface and definition ids, the reference's params and the control selector. */
function canonicalForm(target: ComponentTarget, hit: CanonicalTarget): ComponentTarget {
  return {
    surfaceId: hit.surfaceId,
    path: hit.path.map((definition, i) => {
      const { componentId, params } = hit.reference.path[i]!
      return { componentId: definition.id ?? componentId, ...(params && Object.keys(params).length ? { params } : {}) }
    }),
    ...(target.selector ? { selector: target.selector } : {}),
    ...(target.paramsSource ? { paramsSource: target.paramsSource } : {}),
  }
}
// `paramsSource` decides how a `$` token in a param reads and, where `sourceDecides`, whether a key is dropped or
// stale, so it counts only there: dedupe never merges forms that resolve differently.
// Serialized with sorted keys: this module also runs in the browser bundle, which has no `node:util`.
// `surfaceId` optional: `elementKey` serializes an address whose Surface was dropped.
function addressKey(target: Omit<ComponentTarget, 'surfaceId'> & { surfaceId?: SurfaceId }, sourceDecides: boolean): string {
  const { paramsSource, ...durable } = target
  const address = paramsSource && (sourceDecides || JSON.stringify(durable.path ?? []).includes('$')) ? { ...durable, paramsSource } : durable
  return JSON.stringify(address, (_key, value: unknown) => value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).sort(([x], [y]) => x < y ? -1 : x > y ? 1 : 0)) : value)
}
